<script setup lang="ts">
import { watch, onUnmounted } from 'vue'
import { X } from 'lucide-vue-next'

interface Props {
  open?: boolean
  maxWidth?: string
  showCloseButton?: boolean
  closeOnBackdrop?: boolean
  closeOnEsc?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  maxWidth: '480px',
  showCloseButton: true,
  closeOnBackdrop: true,
  closeOnEsc: true
})

const emit = defineEmits<{
  close: []
}>()

const close = () => {
  emit('close')
}

const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    close()
  }
}

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.open && props.closeOnEsc) {
    close()
  }
}

// Watch for open state to manage body scroll and ESC listener
watch(() => props.open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', handleEscape)
    document.body.style.overflow = ''
  }
})

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="modal-overlay"
        @click.self="handleBackdropClick"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="base-modal__container"
          :style="{ maxWidth }"
          @click.stop
        >
          <!-- Close button -->
          <button
            v-if="showCloseButton"
            type="button"
            class="base-modal__close-button"
            @click="close"
            :aria-label="$t?.('common.close', 'Close') || 'Close'"
          >
            <X :size="20" />
          </button>

          <!-- Header slot -->
          <div v-if="$slots.header" class="base-modal__header">
            <slot name="header" :close="close" />
          </div>

          <!-- Default content slot -->
          <div class="base-modal__content">
            <slot :close="close" />
          </div>

          <!-- Footer slot -->
          <div v-if="$slots.footer" class="base-modal__footer">
            <slot name="footer" :close="close" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Overlay/Backdrop */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
  overflow-y: auto;
}

/* Modal container */
.base-modal__container {
  position: relative;
  width: 100%;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

/* Close button */
.base-modal__close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  color: #64748b;
}

.base-modal__close-button:hover {
  background: white;
  color: #182855;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.base-modal__close-button:active {
  transform: scale(0.95);
}

/* Modal sections */
.base-modal__header {
  padding: 20px 24px 16px;
}

.base-modal__content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.base-modal__footer {
  padding: 16px 24px 20px;
  border-top: 1px solid #e5e7eb;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-active .base-modal__container,
.modal-leave-active .base-modal__container {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .base-modal__container,
.modal-leave-to .base-modal__container {
  transform: scale(0.95);
  opacity: 0;
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .base-modal__container {
    max-width: 100% !important;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    /* Safe area insets for iPhone */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }

  .base-modal__header {
    padding: 16px 20px 12px;
    padding-left: max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
  }

  .base-modal__content {
    padding: 20px 16px;
    overflow-y: visible;
  }

  .base-modal__footer {
    padding: 12px 20px 16px;
    padding-left: max(12px, env(safe-area-inset-left));
    padding-right: max(12px, env(safe-area-inset-right));
  }

  .base-modal__close-button {
    width: 32px;
    height: 32px;
    /* Position close button in safe area */
    top: max(12px, calc(env(safe-area-inset-top) + 8px));
    right: max(12px, calc(env(safe-area-inset-right) + 8px));
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

/* Scrollbar styling */
.base-modal__content::-webkit-scrollbar {
  width: 6px;
}

.base-modal__content::-webkit-scrollbar-track {
  background: transparent;
}

.base-modal__content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.base-modal__content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>
